import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { Alert, AlertsService, BroadcastService, I18NService, UserService } from '@socrates/socrates-shared';
import { User } from '@socrates/common';

export const confirmPasswordValidator: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
	const password = control.get('password');
	const confirm = control.get('confirmPassword');
	return password && confirm && password.value === confirm.value ? null : { 'passwordMismatch': true };
};

const alertsNamespace = 'password-modal';

@Component({
	selector: 'password-modal',
	templateUrl: './passwordModal.component.html',
	standalone: false,
})
export class PasswordModalComponent {
	private _errorMessage: string;
	private _passwordForm: FormGroup;
	private _submitted = false;
	private _submitting = false;
	private _errorMessages: any;
	private canceled = false;
	private readonly _placeholders: any;
	private _formErrors = {
		password: '',
		confirmPassword: ''
	};

	constructor(
		private alertsService: AlertsService,
		private userService: UserService,
		private broadcastService: BroadcastService,
		private formBuilder: FormBuilder
	) {
		this._passwordForm = this.formBuilder.group(
		{
			password: [ '', [ Validators.required, Validators.minLength(8), Validators.pattern(/((?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*\W)){8,16}/) ] ],
			confirmPassword: [ '', [ Validators.required ] ]
		},
		{ validator: confirmPasswordValidator});

		this._placeholders = {
			password: I18NService.getMessage('dashboard.password.placeholders.password'),
			confirm: I18NService.getMessage('dashboard.password.placeholders.confirm'),
		};
		this.alertsService.clearAlerts(alertsNamespace);
	}

	async onSubmit(): Promise<void> {

		if (!this.canceled) {
			if (this._passwordForm.valid) {
				this._submitting = true;
				try {
					const worked: boolean = await this.userService.updateOwnPassword(this._passwordForm.controls.password.value as string);
					this._submitting = false;
					this.broadcastService.broadcast('modalService', { saved: true, worked });
				} catch (error: any) {
					this._submitting = false;
					this._errorMessage = error.message;
					this.alertsService.addAlertIfNotActive(
						new Alert({
							id: 'updateError',
							message: I18NService.getMessage('dashboard.password.errorMsgs.updateError'),
							alertType: 'error',
							namespace: alertsNamespace,
						})
					);
				}
			} else {
				this._submitted = true;
			}
		}
	}

	onCancel(): void {
		this.canceled = true;
		this.broadcastService.broadcast('modalService', { saved: false });
	}

	getMessage(key: string): string {
		return I18NService.getMessage(key);
	}

	get formControls() {
		return this.passwordForm.controls;
	}

	get errorMessage(): string {
		return this._errorMessage;
	}

	get passwordForm(): FormGroup {
		return this._passwordForm;
	}

	set passwordForm(newPasswordForm: FormGroup) {
		this._passwordForm = newPasswordForm;
	}

	get submitted(): boolean {
		return this._submitted;
	}

	get errorMessages(): any {
		return this._errorMessages;
	}

	get formErrors(): any {
		return this._formErrors;
	}

	get placeholders(): any {
		return this._placeholders;
	}

	get submitting(): boolean {
		return this._submitting;
	}
}
